import React, { useState } from "react";
import { ProfilePayload } from "../../types";

interface ProfileInfoProps {
  data: any; // contains main profile data
  formProfile: ProfilePayload;
  setFormProfile: React.Dispatch<React.SetStateAction<ProfilePayload>>;
  onSave: (payload: Partial<ProfilePayload>) => Promise<void>;
}

const PersonalInfo: React.FC<ProfileInfoProps> = ({
  data,
  formProfile,
  setFormProfile,
  onSave,
}) => {
  const [editProfile, setEditProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange =
    (key: keyof ProfilePayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormProfile((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const payload: Partial<ProfilePayload> = {
        user_id: formProfile.user_id,
        full_name: formProfile.full_name?.trim(),
        gender: formProfile.gender,
        dob: formProfile.dob,
        contact_address: formProfile.contact_address?.trim(),
        emergency_contact_person: formProfile.emergency_contact_person?.trim(),
        emergency_contact_number: formProfile.emergency_contact_number?.trim(),
        emergency_contact_address: formProfile.emergency_contact_address?.trim(),
      };

      await onSave(payload);

      setEditProfile(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // reset formProfile back to original data
    setFormProfile({
      user_id: data.user_id,
      full_name: data.full_name || "",
      gender: data.gender || "",
      dob: data.dob || "",
      contact_address: data.contact_address || "",
      emergency_contact_person: data.emergency_contact_person || "",
      emergency_contact_number: data.emergency_contact_number || "",
      emergency_contact_address: data.emergency_contact_address || "",
    });
    setEditProfile(false);
  };

  return (
    <div className="mb-4">
      <h4>
        Personal Info
        <small className="pull-right">
          <span className="edit-icon" style={{ cursor: "pointer" }}>
            {!editProfile ? (
              <i className="bi bi-pencil-square" onClick={() => setEditProfile(true)} />
            ) : (
              <i className="bi bi-x-square bg-danger" onClick={handleCancel} />
            )}
          </span>
        </small>
      </h4>

      {!editProfile ? (
        <div>
          <p><strong>Full Name:</strong> {data.full_name}</p>
          <p><strong>Gender:</strong> {data.gender}</p>
          <p><strong>Date of Birth:</strong> {data.dob}</p>
          <p><strong>Address:</strong> {data.contact_address}</p>
          <p>
            <strong>Emergency Contact:</strong>{" "}
            {data.emergency_contact_person} ({data.emergency_contact_number})
          </p>
          <p><strong>Emergency Address:</strong> {data.emergency_contact_address}</p>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <input
            className="form-control mb-2"
            value={formProfile.full_name}
            onChange={handleChange("full_name")}
            placeholder="Full Name"
            required
          />

          <div className="mb-2">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="gender"
                id="genderMale"
                value="male"
                checked={formProfile.gender === "male"}
                onChange={handleChange("gender")}
              />
              <label className="form-check-label" htmlFor="genderMale">
                Male
              </label>
            </div>

            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="gender"
                id="genderFemale"
                value="female"
                checked={formProfile.gender === "female"}
                onChange={handleChange("gender")}
              />
              <label className="form-check-label" htmlFor="genderFemale">
                Female
              </label>
            </div>
          </div>

          <input
            type="date"
            className="form-control mb-2"
            value={formProfile.dob}
            onChange={handleChange("dob")}
            required
          />

          <textarea
            className="form-control mb-2"
            value={formProfile.contact_address}
            onChange={handleChange("contact_address")}
            placeholder="Address"
          />

          <input
            className="form-control mb-2"
            value={formProfile.emergency_contact_person}
            onChange={handleChange("emergency_contact_person")}
            placeholder="Emergency Contact Person"
          />

          <input
            className="form-control mb-2"
            value={formProfile.emergency_contact_number}
            onChange={handleChange("emergency_contact_number")}
            placeholder="Emergency Contact Number"
          />

          <textarea
            className="form-control mb-2"
            value={formProfile.emergency_contact_address}
            onChange={handleChange("emergency_contact_address")}
            placeholder="Emergency Address"
          />

          <div className="d-block text-right mt-3">
            <button
              type="button"
              className="btn btn-sm btn-danger me-2"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-success"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Submit"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PersonalInfo;
